import {
  Avatar,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiGraphUsers } from '../../api/sdk.gen';
import { GetFolderPermissionsEwsResult, GetUsersMsGraphResult } from '../../api/types.gen';
import { Debouncer } from '../../services/debouncer';
import { ErrorMessageBar } from './ErrorMessageBar';

interface IPeoplePickerProps {
  selectedPeople: GetUsersMsGraphResult[];
  onSelect: (u: GetUsersMsGraphResult[]) => void;
  isDisabled: boolean;
  folderInfo: GetFolderPermissionsEwsResult;
}

export const PeoplePicker = ({ selectedPeople, onSelect, isDisabled, folderInfo }: IPeoplePickerProps) => {
  const { t } = useTranslation();

  const [queryData, setQueryData] = useState<GetUsersMsGraphResult[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (searchString: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApiGraphUsers({ query: { SearchString: searchString } });
      setQueryData(data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  const { debounce, clearDebounce } = useMemo(
    () => Debouncer({ delayInMs: 400, callback: (args: { searchString: string }) => fetch(args.searchString) }),
    [fetch],
  );

  useEffect(() => {
    return () => {
      clearDebounce();
    };
  }, [clearDebounce]);

  const availableUser =
    queryData?.map((u) => {
      return {
        mail: u.mail,
        displayName: u.displayName,
        isDisabled: folderInfo.permissions?.map((x) => x?.email).includes(u.mail),
      };
    }) ?? [];

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (_, data) => {
    const userKey = data.value;
    const userWasRemoved = data.selectedOptions.includes(userKey) === false;

    if (userWasRemoved) {
      onSelect(selectedPeople.filter((sp) => getEmailFromUser(sp) !== userKey));
      return;
    }

    if (data.selectedOptions.includes(NO_ITEM_VALUE)) {
      return;
    }

    const selectedUsers =
      availableUser?.filter((sp) => data.selectedOptions.some((d) => d === getEmailFromUser(sp)) && !sp.isDisabled) ?? [];

    // hier die doppelten löschen!
    const uniqueArr = [...selectedPeople, ...selectedUsers].filter(
      (obj, index, self) => index === self.findIndex((o) => o.mail === obj.mail),
    );
    onSelect(uniqueArr);
    fetch('');
  };

  const options = availableUser?.filter((p) => !selectedPeople.some((sp) => getEmailFromUser(sp) === getEmailFromUser(p))) ?? [];

  return (
    <>
      {error && <ErrorMessageBar messageBarTitle={t('peoplePicker.errors.anErrorOccurred')} error={error} />}
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedPeople.map(getEmailFromUser)}
        disabled={isDisabled}
        positioning="below"
      >
        <TagPickerControl>
          <TagPickerGroup aria-label="Selected Employees">
            {selectedPeople.map((people, index) => (
              <Tag
                key={`selectable-${getEmailFromUser(people)}-${index}`}
                shape="rounded"
                media={<Avatar aria-hidden name={people.displayName ?? ''} color="colorful" />}
                value={getEmailFromUser(people)}
              >
                {people.displayName}
              </Tag>
            ))}
          </TagPickerGroup>
          <TagPickerInput aria-label="Select Employees" onChange={(e) => debounce({ searchString: e.target.value })} />
          {loading && <Spinner size="extra-tiny" />}
        </TagPickerControl>
        <TagPickerList style={{ height: LIST_HEIGHT_PX }}>
          {options.length === 0 ? (
            <TagPickerOption value={NO_ITEM_VALUE}>{t('permissions.modifyAction.personGroup.noItems')}</TagPickerOption>
          ) : (
            options.map((people, index) => (
              <TagPickerOption
                secondaryContent={people.mail ?? ''}
                media={<Avatar shape="square" aria-hidden name={people.displayName ?? ''} color="colorful" />}
                value={getEmailFromUser(people)}
                key={`option-${getEmailFromUser(people)}-${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  color: people.isDisabled ? 'GrayText' : undefined,
                }}
                text={people.displayName ?? ''}
              >
                {
                  (
                    <div style={{ display: 'flex' }}>
                      {people.isDisabled ? (
                        <>
                          <del>{people.displayName ?? ''}</del>
                          {t('permissions.modifyAction.personGroup.alreadySelected')}
                        </>
                      ) : (
                        people.displayName
                      )}
                    </div>
                  ) as unknown as string
                }
              </TagPickerOption>
            ))
          )}
        </TagPickerList>
      </TagPicker>
    </>
  );
};

const NO_ITEM_VALUE = 'noItem';

function getEmailFromUser(user: GetUsersMsGraphResult): string {
  return user?.mail ?? '';
}

const NUMBER_OF_PEOPLE_TO_DISPLAY = 5;
const OPTION_HEIGHT = 52;
const LIST_PADDING_Y = 4;
const LIST_HEIGHT_PX = `${NUMBER_OF_PEOPLE_TO_DISPLAY * OPTION_HEIGHT + 2 * LIST_PADDING_Y}px`;
